import React, { useEffect, useState } from 'react';
import { Form, Button, Card, Alert, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';
import { NetworkProvider } from '../../NetworkProvider';

export default function AuthForm({
  registrationToggle,
  submitRegistration,
  submitLogin,
  errorMessage,
  successMessage,
  email,
  setEmail,
  validateEmail,
  error,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const allowManualRegister = process.env.REACT_APP_ALLOW_MANUAL_REGISTER === 'true';

  // New state to track which view to show: 'sso', 'login', or 'register'
  const [viewState, setViewState] = useState('sso');

  useEffect(() => {
    const { email } = location.state || {};
    if (email) {
      setEmail(email)
    }
  }, []);

  const initiateVPN = () => {
    NetworkProvider.initiateVPMLogin()
  }

  // If registration is toggled and manual registration is not allowed, only show SSO
  const showOnlySSO = registrationToggle && !allowManualRegister;

  // SSO-only view (initial state)
  if (viewState === 'sso') {
    return (
      <Card className="w-100 p-4 shadow-lg rounded" style={{ border: "none", maxWidth: 550 }}>
        <h4 className="text-center mb-4">
          {t("Pieteikties")}
        </h4>

        {successMessage ? (
          <Alert variant="success">{successMessage}</Alert>
        ) : errorMessage ? (
          <Alert variant="danger">{errorMessage}</Alert>
        ) : null}

        <Button
          variant="primary"
          type="button"
          className="w-100 rounded-sm"
          onClick={() => initiateVPN()}>
          {t("Vienotā pieteikšanās")}
        </Button>

        <div className="mt-3 text-center">
          <Button
            variant="link"
            onClick={() => setViewState('login')}
            className="text-primary p-0"
            style={{ fontSize: "14px", display: "inline-flex", alignItems: "center" }}
          >
            {t("Pieteikties ar e-pastu")}
          </Button>
        </div>
      </Card>
    );
  }

  // Login view
  if (viewState === 'login') {
    return (
      <Card className="w-100 p-4 shadow-lg rounded" style={{ border: "none", maxWidth: 550 }}>
        <h4 className="text-center mb-4">
          {t("Pieteikties")}
        </h4>
        <Form onSubmit={submitLogin}>
          {successMessage ? (
            <Alert variant="success">{successMessage}</Alert>
          ) : errorMessage ? (
            <Alert variant="danger">{errorMessage}</Alert>
          ) : null}

          <Form.Group className="mb-3 w-100">
            <Form.Label className="d-block text-start w-100">{t("E-pasta adrese")}</Form.Label>
            <Form.Control
              type="email"
              placeholder={t("Ievadiet e-pasta adresi")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={validateEmail}
              className={`rounded-sm ${error ? "is-invalid" : ""}`}
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </Form.Group>

          <Form.Group className="mb-3 w-100">
            <Form.Label className="d-block text-start w-100">{t("Parole")}</Form.Label>
            <Form.Control
              type="password"
              placeholder={t("Ievadiet paroli")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-sm"
            />
          </Form.Group>

          <Button variant="primary" type="submit" className="w-100 rounded-sm mb-2">
            {t("Pieteikties")}
          </Button>
        </Form>

        <div className="mt-3 text-center">
          <div style={{ fontSize: "14px", marginBottom: "8px" }}>
            <Button
              variant="link"
              onClick={() => initiateVPN()}
              className="text-primary p-0"
              style={{ fontSize: "14px" }}
            >
              {t("Vienotā pieteikšanās")}
            </Button>
          </div>

          {allowManualRegister && (
            <div style={{ fontSize: "14px" }}>
              <Button
                variant="link"
                onClick={() => setViewState('register')}
                className="text-primary p-0"
                style={{ fontSize: "14px", marginLeft: 3 }}
              >
                {t("Reģistrēties")}
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Registration view
  return (
    <Card className="w-100 p-4 shadow-lg rounded" style={{ border: "none", maxWidth: 550 }}>
      <h4 className="text-center mb-4">
        {t("Reģistrācija")}
      </h4>
      <Form onSubmit={submitRegistration}>
        {successMessage ? (
          <Alert variant="success">{successMessage}</Alert>
        ) : errorMessage ? (
          <Alert variant="danger">{errorMessage}</Alert>
        ) : null}

        <Form.Group className="mb-3 w-100">
          <Form.Label className="d-block text-start w-100">{t("E-pasta adrese")}</Form.Label>
          <Form.Control
            type="email"
            placeholder={t("Ievadiet e-pasta adresi")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validateEmail}
            className={`rounded-sm ${error ? "is-invalid" : ""}`}
          />
          {error && <div className="invalid-feedback">{error}</div>}
        </Form.Group>

        <Form.Group className="mb-3 mb-md-0 w-100">
          <Row className="g-3 row-cols-1 row-cols-md-2">
            <Col className="w-50">
              <Form.Label className="d-block text-start">{t("Vārds")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("Ievadiet vārdu")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="rounded-sm"
              />
            </Col>
            <Col className="w-50">
              <Form.Label className="d-block text-start">{t("Uzvārds")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("Ievadiet uzvārdu")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="rounded-sm"
              />
            </Col>
          </Row>
        </Form.Group>

        <Form.Group className="mb-3 w-100">
          <Form.Label className="d-block text-start w-100">{t("Parole")}</Form.Label>
          <Form.Control
            type="password"
            placeholder={t("Ievadiet paroli")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-sm"
          />
        </Form.Group>

        <Form.Group className="mb-3 w-100">
          <Form.Label className="d-block text-start w-100">{t("Apstipriniet paroli")}</Form.Label>
          <Form.Control
            type="password"
            placeholder={t("Atkārtoti ievadiet paroli")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="rounded-sm"
          />
        </Form.Group>

        <Button variant="primary" type="submit" className="w-100 rounded-sm mb-2">
          {t("Reģistrēties")}
        </Button>

        <Button
          variant="primary"
          type="button"
          className="w-100 rounded-sm mt-2"
          onClick={() => initiateVPN()}>
          {t("Vienotā pieteikšanās")}
        </Button>
      </Form>

      <div className="mt-3 text-center">
        <span style={{ fontSize: "14px", display: "inline-flex", alignItems: "center" }}>
          {t("Jau ir lietotāja konts? ")}
          <Button
            variant="link"
            onClick={() => setViewState('login')}
            className="text-primary p-0"
            style={{ fontSize: "14px", display: "inline-flex", marginLeft: 3, alignItems: "center" }}
          >
            {t("Pieteikties")}
          </Button>
        </span>
      </div>
    </Card>
  );
}